import '.ex{column-gap:1rem;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import { styled } from 'inlines'
import { navigate } from 'wouter/use-browser-location'
import { fullName } from '@app/util/helpers'
import { DiscussionCard } from '@app/components/blocks/discussions/DiscussionCard'
import { SectionHeader } from '../../sectionHeader/SectionHeader'
import { Title } from '@app/components/core/typography/Title'
import { useTranslations } from '@i18n/useLanguage'
import { useRelativeDateFormat } from '@app/util/useDateFormat'
import useMediaQuery from '@app/util/useMediaQuery'
import { useAuthState, useClient } from '@based/sdk/react'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import {
  getPostedInLabel,
  getPostShareUrl,
  getPostShareTitle,
} from '@app/pages/discussions/discussionsHelpers'
import { PostViewModel } from 'functions/app/thread/view-latest'
import { AppearItem } from '../blocks/AppearInViewport'

export type ThreadContentSectionType = {
  type: 'thread'
  title: string
  titleLevel?: 'H1' | 'H2' | 'H3' | 'H4'
  seeMoreLabel?: string
  seeMoreLink?: string
  items: PostViewModel[]
  columns?: 2 | 3
}

export const ThreadContentSection = ({
  title,
  titleLevel = 'H4',
  seeMoreLabel,
  seeMoreLink,
  items,
  columns = 2,
}: ThreadContentSectionType) => {
  const t = useTranslations().app
  const relativeDate = useRelativeDateFormat()
  const isMobile = useMediaQuery()
  const authState = useAuthState()
  const client = useClient()
  const { withAuthCheck } = useAuthModal()
  const visibleItems = items.slice(0, 6)

  const handleLike = (postId: number) => {
    void withAuthCheck(async () => {
      await client.call('react-post', { postId })
    })
  }

  if (visibleItems.length === 0) return null

  return (
    <Column alignItems="stretch" gap={0}>
      <AppearItem staggerIndex={0}>
        {seeMoreLink ? (
          <SectionHeader
            title={title}
            titleLevel={titleLevel}
            seeMore={seeMoreLabel ?? t.common.seeMore}
            toRoute={seeMoreLink}
          />
        ) : (
          <Title.Hx title={titleLevel}>{title}</Title.Hx>
        )}
      </AppearItem>
      <div className="ex"
        style={{
          columnCount: isMobile ? 1 : columns,}}
      >
        {visibleItems.map((post, index) => {
          const likedUserIds = (post.viewers ?? [])
            .filter((v) => v.$reaction === 'like')
            .map((v) => v.id.toString())
          const liked =
            !!authState.userId && likedUserIds.includes(authState.userId.toString())

          return (
            <AppearItem
              key={post.id}
              staggerIndex={index + 1}
              style={{ breakInside: 'avoid', marginBottom: '1rem' }}
            >
              <DiscussionCard
                authorName={post.author ? fullName(post.author) : t.common.unknown}
                authorAvatar={post.author?.avatar?.src}
                postedIn={getPostedInLabel(post.threads)}
                date={post.createdAt ? relativeDate(new Date(post.createdAt)) : ''}
                updatedAt={
                  post.updatedAt && post.updatedAt > (post.createdAt ?? 0)
                    ? relativeDate(new Date(post.updatedAt))
                    : undefined
                }
                title={post.title ?? undefined}
                content={post.content ?? ''}
                likes={likedUserIds.length}
                liked={liked}
                onLikeClick={() => handleLike(post.id)}
                replies={post.replies?.length ?? 0}
                categories={post.categories}
                media={(post.media ?? [])
                  .filter((item) => item.status === 'ready')
                  .map((item) => ({
                    src: item.src,
                    mimeType: item.mimeType,
                    name: item.name,
                  }))}
                shareUrl={getPostShareUrl(post.id)}
                shareTitle={getPostShareTitle(post.title ?? undefined, post.content ?? undefined)}
                onClick={
                  seeMoreLink
                    ? () => navigate(`${seeMoreLink}?post=${post.id}`)
                    : undefined
                }
              />
            </AppearItem>
          )
        })}
      </div>
    </Column>
  )
}
